import { sha256 } from 'js-sha256';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { readAuthTokenFromCookie } from '@/utils/tools';
import { resolveCartSelectionIdentity } from './cart-selection-identity';

interface CartV1RecentAddScope {
  identity: string;
  site: string;
  currency: string;
  /** 仅在请求内存中校验登录会话，不写入存储。 */
  authFingerprint: string;
  /** 仅用于同一登录会话的资料恢复校验，不保存额外的会话数据。 */
  sessionIdentity: string | null;
}

export function readCartV1RecentAddScope(): CartV1RecentAddScope | null {
  if (typeof window === 'undefined') return null;
  try {
    // 与 Store 的认证初始化保持一致：zl-token 是本地兜底键，不是登录 Cookie。
    const token = readAuthTokenFromCookie();
    const identity = resolveCartSelectionIdentity({
      authenticated: Boolean(token),
      userId: localStorage.getItem('zl-userId'),
      visitorId: localStorage.getItem('zl-visitor_id'),
      token,
    });
    const savedCurrency = localStorage.getItem('zl-currency');
    const currency: unknown = savedCurrency ? JSON.parse(savedCurrency) : null;
    const currencyName =
      currency &&
      typeof currency === 'object' &&
      'name' in currency &&
      typeof currency.name === 'string'
        ? currency.name
        : env.NEXT_PUBLIC_DEFAULT_CURRENCY;
    if (!identity || !currencyName) return null;
    return {
      identity,
      site: env.NEXT_PUBLIC_SITE_NAME || 'us',
      currency: currencyName,
      authFingerprint: token ? sha256(token) : '',
      sessionIdentity: token ? resolveCartSelectionIdentity({ authenticated: true, token }) : null,
    };
  } catch (error) {
    logger.error('Failed to read V1 cart add scope:', error);
    return null;
  }
}

/** 成功加购的可选 sidecar；旧请求不能重标记或覆盖新身份的加购。 */
export function recordCartV1RecentAdd(ids: number[], requestScope: CartV1RecentAddScope | null) {
  if (typeof window === 'undefined') return;
  try {
    const recentAddKey = 'zl-cart-v1-recent-add:v1';
    const current = readCartV1RecentAddScope();
    if (!current) {
      localStorage.removeItem(recentAddKey);
      return;
    }
    if (
      !requestScope ||
      (current.identity !== requestScope.identity &&
        requestScope.identity !== current.sessionIdentity) ||
      current.site !== requestScope.site ||
      current.currency !== requestScope.currency ||
      current.authFingerprint !== requestScope.authFingerprint ||
      !Array.isArray(ids) ||
      !ids.length ||
      !ids.every((id) => typeof id === 'number' && Number.isSafeInteger(id) && id > 0)
    )
      return;
    const now = Date.now();
    let pendingIds: number[] = [];
    // 购物车消费前可能连续加购；只累积同作用域、未过期且合法的成功目标。
    try {
      const raw = localStorage.getItem(recentAddKey);
      const pending: unknown = raw ? JSON.parse(raw) : null;
      if (
        pending &&
        typeof pending === 'object' &&
        'version' in pending &&
        pending.version === 1 &&
        'identity' in pending &&
        (pending.identity === current.identity ||
          (current.sessionIdentity && pending.identity === current.sessionIdentity)) &&
        'site' in pending &&
        pending.site === current.site &&
        'currency' in pending &&
        pending.currency === current.currency &&
        'createdAt' in pending &&
        typeof pending.createdAt === 'number' &&
        Number.isFinite(pending.createdAt) &&
        pending.createdAt <= now &&
        now - pending.createdAt < 5 * 60_000 &&
        'cartIds' in pending &&
        Array.isArray(pending.cartIds) &&
        pending.cartIds.every(
          (id): id is number => typeof id === 'number' && Number.isSafeInteger(id) && id > 0,
        )
      )
        pendingIds = pending.cartIds;
    } catch {
      // 损坏的旧凭据不能阻止本次成功加购。
    }
    localStorage.setItem(
      recentAddKey,
      JSON.stringify({
        version: 1,
        cartIds: Array.from(new Set([...pendingIds, ...ids])),
        createdAt: now,
        identity: current.identity,
        site: current.site,
        currency: current.currency,
      }),
    );
  } catch (error) {
    logger.error('Failed to record V1 cart add:', error);
  }
}
